// The asset page's derived figures (asset-detail-revamp-handover §3.3–3.4): days on site, when "last seen" is stale,
// and how complete the record is. Pure functions, so the rules are unit-tested rather than read off a screen.
import { todayIn } from './time';

/** "Last seen" turns amber after this many days. One constant for now; per category later (handover §11 Q2). */
export const STALE_AFTER_DAYS = 7;

const DAY = 86_400_000;

/**
 * Stale only for assets that are *heard* (a BLE tag or tracker, which a gateway or a passing phone reports on its
 * own). A QR-only asset is seen when somebody scans it, so an old date is normal for it and not a warning.
 */
export function isStale(
  lastSeenAt: string | null | undefined,
  hasElectronicTag: boolean,
  now: number = Date.now(),
): boolean {
  if (!hasElectronicTag || !lastSeenAt) return false;
  return now - Date.parse(lastSeenAt) > STALE_AFTER_DAYS * DAY;
}

export interface LocationEvent {
  event: string;
  site_id: string | null;
  at: string;
}

/**
 * Whole calendar days (company timezone) since the most recent "entered" event at the site the asset is on now;
 * 0 = today. null when it is not on site or there is no such event (the tile shows "—").
 */
export function daysOnSite(
  events: readonly LocationEvent[],
  siteId: string | null | undefined,
  timeZone: string,
  now: Date = new Date(),
): number | null {
  if (!siteId) return null;
  const entered = events
    .filter((e) => e.event === 'entered' && e.site_id === siteId)
    .reduce<string | null>((latest, e) => (!latest || e.at > latest ? e.at : latest), null);
  if (!entered) return null;
  const from = todayIn(timeZone, new Date(entered));
  const to = todayIn(timeZone, now);
  return Math.max(
    0,
    Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY),
  );
}

/** What makes a record complete (handover §3.4), equal weight. Change the list here, nowhere else. */
export const COMPLETENESS_FIELDS = ['make_model', 'serial', 'value', 'photo', 'mounting'] as const;
export type CompletenessField = (typeof COMPLETENESS_FIELDS)[number];

export interface CompletenessInput {
  makeModel: string | null | undefined;
  serial: string | null | undefined;
  hasValue: boolean;
  photos: number;
  mountingPhotos: number;
  /** the mounting photo only counts for an asset with a device on it — a QR label has nothing to mount */
  hasElectronicTag: boolean;
}

export function completeness(r: CompletenessInput): {
  pct: number;
  missing: CompletenessField[];
} {
  const applies = COMPLETENESS_FIELDS.filter((f) => f !== 'mounting' || r.hasElectronicTag);
  const done: Record<CompletenessField, boolean> = {
    make_model: !!r.makeModel?.trim(),
    serial: !!r.serial?.trim(),
    value: r.hasValue,
    photo: r.photos > 0,
    mounting: r.mountingPhotos > 0,
  };
  const missing = applies.filter((f) => !done[f]);
  return { pct: Math.round((100 * (applies.length - missing.length)) / applies.length), missing };
}
